import { SupplierCell } from "@/components/shared/cells/supplier";
import type { InvoiceRowData } from "@/components/shared/data-table";
import { ProcessingStates } from "@/components/shared/miscellaneous/processing-states";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePermissions } from "@/hooks/usePermissions";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { confColor } from "@/lib/miscellaneous/confidence-color";
import { cn } from "@/lib/utils";
import { useTenant } from "@/providers/session";
import type { CellContext } from "@tanstack/react-table";
import { Scissors } from "lucide-react";

type Props = CellContext<InvoiceRowData, unknown> & {
  showConfidence?: boolean;
};

export const SupplierNameCell = ({ row, showConfidence }: Props) => {
  const { isSuperAdmin } = usePermissions();
  const { isOcrReviewRequired } = useTenant();

  if (row.original.invoice.status === InvoiceStatus.NEW) {
    return <ProcessingStates invoiceId={row.original.invoice.invoiceId} />;
  }

  if (
    (isOcrReviewRequired &&
      row.original.invoice.ocrReviewed === false &&
      !isSuperAdmin) ||
    row.original.invoice.status === InvoiceStatus.ERROR
  ) {
    return (
      <div className="flex items-center justify-start h-10 pl-4 pr-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: "100%",
              }}
            >
              <span
                className={cn(
                  "flex-1 overflow-hidden whitespace-nowrap text-ellipsis",
                )}
              >
                {row.original.invoice.fileName}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{row.original.invoice.fileName}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  } else if (row.original.invoice.splitPdf === true) {
    return (
      <div className="flex items-center justify-start h-10 pl-4 pr-1">
        <Scissors className="min-h-[24px] min-w-[24px] mr-2 text-gray-500" />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: "100%",
              }}
            >
              <span
                className={cn(
                  "flex-1 overflow-hidden whitespace-nowrap text-ellipsis",
                )}
              >
                {row.original.invoice.fileName}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{row.original.invoice.fileName}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  let supplierConfidence = null;

  if (row.original?.invoice?.supplierMatchConfidence === null)
    supplierConfidence = null;
  else if (row.original?.invoice?.supplierMatchConfidence === 1)
    supplierConfidence = 1;
  else if (
    row.original?.invoice?.supplierMatchConfidence === 0 ||
    row.original?.supplier?.status !== "CONFIRMED"
  )
    supplierConfidence = 0;

  const name = row.original?.supplier?.supplierName || "Unbekannt";
  const address = row.original?.supplier?.address ?? undefined;

  return (
    <SupplierCell
      name={name}
      address={address}
      className={cn(
        "rounded p-1",
        confColor(showConfidence ? supplierConfidence : undefined),
      )}
    />
  );
};
